'use client';
import { useEffect, useState } from 'react';

interface UsePaginationProps {
  count?: number;
  disabled?: boolean;
  onChange?: (value: number) => void;
  page?: number;
  siblingCount?: number;
  reset?: () => void;
}

type PaginationItemType = 'page' | 'next' | 'previous' | 'ellipsis';

interface PaginationItem {
  onClick: (value: number | null) => void;
  type: PaginationItemType;
  page: number | null;
  selected: boolean;
  disabled: boolean;
  'aria-current'?: 'page';
}

function range(start: number, end: number) {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function usePagination({
  count = 1,
  disabled = false,
  onChange,
  page: propsPage = 1
}: UsePaginationProps = {}) {
  const boundaryCount = 1;
  const siblingCount = 1;

  const [page, setPage] = useState(propsPage);

  useEffect(() => {
    setPage(propsPage);
  }, [propsPage]);

  const reset = () => {
    setPage(propsPage);
  };

  const handleClick = (value: number | null) => {
    if (value == null || value < 1 || value > count) {
      return;
    }

    setPage(value);
    onChange?.(value);
  };

  const siblingsStart = Math.max(
    Math.min(page - siblingCount, count - boundaryCount - siblingCount * 2 - 1),
    boundaryCount + 2
  );
  const siblingsEnd = Math.min(
    Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
    count - boundaryCount - 1
  );

  const itemList: (number | PaginationItemType)[] = [
    'previous' as const,
    1,
    ...(siblingsStart > boundaryCount + 2
      ? ['ellipsis' as const]
      : boundaryCount + 1 < count - boundaryCount
        ? [boundaryCount + 1]
        : []),
    ...range(siblingsStart, siblingsEnd),
    ...(siblingsEnd < count - boundaryCount - 1
      ? ['ellipsis' as const]
      : count - boundaryCount > boundaryCount
        ? [count - boundaryCount]
        : []),
    count,
    'next' as const
  ];

  const buttonPage = (type: PaginationItemType): number | null => {
    switch (type) {
      case 'previous':
        return page - 1;
      case 'next':
        return page + 1;
      default:
        return null;
    }
  };

  const items: PaginationItem[] = itemList.map((item) =>
    typeof item === 'number'
      ? {
          onClick: () => handleClick(item),
          type: 'page',
          page: item,
          selected: item === page,
          disabled,
          'aria-current': item === page ? 'page' : undefined
        }
      : {
          onClick: () => handleClick(buttonPage(item)),
          type: item,
          page: buttonPage(item),
          selected: false,
          disabled:
            disabled ||
            (item !== 'ellipsis' &&
              (item === 'next' ? page >= count : page <= 1))
        }
  );

  const nextPage = () => {
    if (page >= count) {
      return;
    }

    setPage(page + 1);
    onChange?.(page + 1);
  };

  return { items, reset, currentPage: page, nextPage };
}
